import { PublicClientApplication } from "@azure/msal-browser";
import { loginRequest } from "../config/msalConfig";
import {
  clearCachedToken as clearProviderToken,
  getAccessToken as getProviderToken,
  getMsalInstanceOrNull,
  setMsalInstance as setProviderMsalInstance,
} from "./tokenProvider";

// localStorage keys for user preferences persistence
const INSTANCE_STORAGE_KEY = "sky-selected-instance-id";
const TENANT_STORAGE_KEY = "sky-selected-tenant-id";
const THEME_STORAGE_KEY = "sky-user-theme";
const LANGUAGE_STORAGE_KEY = "sky-user-language";

/**
 * Auth Service for non-React contexts (utilities, services, etc.)
 *
 * This service provides authentication functionality without React hooks,
 * making it usable in utility files, service files, and other non-component code.
 *
 * Usage:
 *   import { authService } from "../utils/authService";
 *   const token = await authService.getAccessToken();
 */

/**
 * Set the MSAL instance (called from index.tsx during initialization)
 *
 * The instance and the token cache both live in `tokenProvider` now. Keeping a
 * second cache here would reintroduce exactly the defect this fix removes: two
 * caches expire independently, so one client can be holding a dead token while
 * the other is fine, and which one you get depends on the call site.
 */
export function setMsalInstance(instance: PublicClientApplication): void {
  setProviderMsalInstance(instance);
}

/**
 * Clear the cached token (call on logout or when token becomes invalid)
 */
export function clearCachedToken(): void {
  clearProviderToken();
}

/**
 * Get the MSAL instance
 */
function getMsalInstance(): PublicClientApplication {
  const instance = getMsalInstanceOrNull();
  if (!instance) {
    throw new Error("MSAL instance not initialized. Call setMsalInstance first.");
  }
  return instance;
}

/**
 * Check if user is logged in
 */
export function isLoggedIn(): boolean {
  const instance = getMsalInstance();
  const accounts = instance.getAllAccounts();
  return accounts.length > 0;
}

/**
 * Get access token, acquiring a fresh one when the cached copy is spent.
 *
 * Delegates to `tokenProvider`, which owns the cache, deduplicates concurrent
 * acquisitions and decides when a failure warrants sending the user to B2C.
 *
 * Throws rather than returning null on failure — that is this function's
 * long-standing contract, and callers interpolate the result straight into an
 * `Authorization` header, where a null would be sent as the literal "Bearer
 * null" and come back as an opaque 401.
 */
export async function getAccessToken(): Promise<string> {
  const token = await getProviderToken();
  if (!token) {
    throw new Error("Unable to acquire an access token");
  }
  return token;
}

/**
 * Get authorization header for API calls
 */
export async function getAuthorizationHeader(): Promise<{
  "Authorization": string;
  "InstanceId": string;
  "Accept": string;
  "Content-Type": string;
}> {
  const token = await getAccessToken();
  const instanceId = getInstanceId();
  return {
    "Authorization": `Bearer ${token}`,
    "InstanceId": instanceId || "",
    "Accept": "application/json",
    "Content-Type": "application/json",
  };
}

/**
 * Get authorization header for multipart requests
 */
export async function getMultiPartAuthorizationHeader(): Promise<{
  Authorization: string;
  InstanceId: string;
  Accept: string;
}> {
  return getAuthorizationHeader();
}

/**
 * Get combined headers (authorization + content-type)
 */
export async function getHeader(): Promise<{
  "Authorization": string;
  "Content-Type": string;
  "InstanceId": string;
  "Accept": string;
}> {
  const token = await getAccessToken();
  const instanceId = getInstanceId();
  return {
    "Authorization": `Bearer ${token}`,
    "Content-Type": "application/json",
    "InstanceId": instanceId || "",
    "Accept": "application/json",
  };
}

/**
 * Get instance ID from URL query string or localStorage
 */
export function getInstanceId(): string {
  // First check URL query string
  const urlParams = new URLSearchParams(window.location.search);
  const urlInstanceId = urlParams.get("instanceid") || urlParams.get("instanceId");
  if (urlInstanceId) {
    return urlInstanceId;
  }

  // Fallback to localStorage
  return localStorage.getItem(INSTANCE_STORAGE_KEY) || "";
}

/**
 * Get query string with instance ID
 */
export function getQueryString(): string {
  const instanceId = getInstanceId();
  return instanceId ? `?instanceid=${instanceId}` : "";
}

/**
 * Get saved tenant ID from localStorage
 */
export function getSavedTenantId(): string {
  return localStorage.getItem(TENANT_STORAGE_KEY) || "";
}

/**
 * Get saved instance ID from localStorage
 */
export function getSavedInstanceId(): string {
  return localStorage.getItem(INSTANCE_STORAGE_KEY) || "";
}

/**
 * Save tenant ID to localStorage
 */
export function saveTenantId(tenantId: string): void {
  if (tenantId) {
    localStorage.setItem(TENANT_STORAGE_KEY, tenantId);
  }
}

/**
 * Save instance ID to localStorage
 */
export function saveInstanceId(instanceId: string): void {
  if (instanceId) {
    localStorage.setItem(INSTANCE_STORAGE_KEY, instanceId);
  }
}

/**
 * Get saved theme from localStorage
 */
export function getSavedTheme(): string {
  return localStorage.getItem(THEME_STORAGE_KEY) || "Light";
}

/**
 * Save theme to localStorage
 */
export function saveTheme(theme: string): void {
  if (theme) {
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  }
}

/**
 * Get saved language from localStorage
 */
export function getSavedLanguage(): string {
  return localStorage.getItem(LANGUAGE_STORAGE_KEY) || "English";
}

/**
 * Save language to localStorage
 */
export function saveLanguage(language: string): void {
  if (language) {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
  }
}

/**
 * Get current user information
 */
export function currentUser(): any {
  const instance = getMsalInstance();
  const accounts = instance.getAllAccounts();

  if (accounts.length === 0) {
    return null;
  }

  const account = accounts[0];
  const idTokenClaims = account.idTokenClaims as any;

  return {
    id: account.localAccountId,
    name: account.name || idTokenClaims?.name || "",
    email: account.username || idTokenClaims?.email || "",
    roles: idTokenClaims?.roles || [],
    isAccountEnabled: idTokenClaims?.isAccountEnabled !== false,
  };
}

/**
 * Clear all user preferences from storage
 * Called on logout to prevent cross-user data leakage
 */
export function clearInstanceSelection(): void {
  // Clear cached token
  clearCachedToken();

  if (typeof localStorage !== "undefined") {
    localStorage.removeItem(INSTANCE_STORAGE_KEY);
    localStorage.removeItem(TENANT_STORAGE_KEY);
    localStorage.removeItem(THEME_STORAGE_KEY);
    localStorage.removeItem(LANGUAGE_STORAGE_KEY);
  }
  sessionStorage.clear();
}

/**
 * Logout user
 * Clears instance selection and session data before redirecting
 */
export async function logout(): Promise<void> {
  const instance = getMsalInstance();

  // Clear instance selection
  clearInstanceSelection();

  // Redirect to logout
  await instance.logoutRedirect();
}

/**
 * Login with redirect
 *
 * @param returnTo Optional path (e.g. "/scheduled-tours?instanceid=...") to navigate to after
 * sign-in. When provided, the path is preserved across the auth round-trip via two channels:
 *   1. OAuth `state` parameter (primary): travels through the auth provider, so it survives
 *      sessionStorage being wiped by privacy modes, extensions, or MSAL cache resets.
 *   2. sessionStorage (fallback): kept for defense in depth.
 * msalConfig.createMsalInstance reads `response.state` post-redirect and rehydrates
 * sessionStorage; callback.tsx then navigates to the saved path.
 */
export async function login(returnTo?: string): Promise<void> {
  const instance = getMsalInstance();
  const shouldPreserve =
    typeof returnTo === "string" && returnTo !== "/" && !returnTo.startsWith("/auth");
  if (shouldPreserve && returnTo) {
    sessionStorage.setItem("redirectAfterLogin", returnTo);
  }
  await instance.loginRedirect({
    ...loginRequest,
    ...(shouldPreserve && returnTo ? { state: returnTo } : {}),
  });
}

/**
 * Recover from a 401 WITHOUT ending the tenant-wide B2C session.
 *
 * The obvious move on "the server rejected your token" is to log out and start
 * over. That is what this app used to do, and it is wrong here: `logout()`
 * calls `logoutRedirect`, which hits B2C's **end_session** endpoint and tears
 * down the SSO session SHARED BY EVERY SKYPOINT APP. One stale token in SkyCRM
 * would sign the operator out of SkyVoice and Studio too — apps they may have
 * open and mid-task in other tabs. SkyVoice hit this first and fixed it the
 * same way; this is that fix ported.
 *
 * What is actually broken in a 401 is the LOCAL token cache, so that is all
 * this clears. `login()` then re-authenticates against the still-valid shared
 * session, which normally round-trips silently with no credential prompt.
 *
 * Unlike SkyVoice's version this preserves where the user was: our `login()`
 * carries the current path through the OAuth `state` parameter, so they land
 * back on the screen they were using rather than the dashboard.
 */
export async function reauthenticate(): Promise<void> {
  const instance = getMsalInstance();

  // Our in-memory copy is exactly as stale as MSAL's; leaving it would hand the
  // rejected token straight back to the next request.
  clearCachedToken();
  await instance.clearCache();

  await login(`${window.location.pathname}${window.location.search}`);
}

/**
 * Auth service object (for backward compatibility)
 */
export const authService = {
  setMsalInstance,
  isLoggedIn,
  getAccessToken,
  getAuthorizationHeader,
  getMultiPartAuthorizationHeader,
  getHeader,
  getInstanceId,
  getQueryString,
  getSavedTenantId,
  getSavedInstanceId,
  saveTenantId,
  saveInstanceId,
  getSavedTheme,
  saveTheme,
  getSavedLanguage,
  saveLanguage,
  currentUser,
  logout,
  login,
  reauthenticate,
  clearInstanceSelection,
  clearCachedToken,

  // Getter for MSAL instance (for advanced use cases)
  get msalInstance() {
    return getMsalInstance();
  },
};
